import React, { useEffect, useState, useCallback, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import moment from 'moment';
import { useQuery } from 'hooks';
import { buildUrl } from 'utils';
import Title from 'components/Title';
import { Table } from 'components/UI';
import Layout from 'components/Layout';
import { useStyles } from './List.styled';
import { useHistory, useLocation } from 'react-router-dom';
import { formatAmount, toQueryString } from 'utils';
import { SearchField } from 'components/FormFields';
import { FETCH_STATE } from 'constants/common';
import { UsersSelect, PartnersSelect } from 'components/AsyncFields';
import { TransactionsAction } from 'state/modules/transaction';
import { PaymentActions } from 'state/modules/payment';
import { Grid, Paper, Container } from '@material-ui/core';
import { PaymentMethodSelect, PaymentStatusSelect } from 'components/DataFields';
import { statusStyles } from 'pages/AccountStatement/Common.styled';
import { UserContext } from 'context';
import { debounce } from 'lodash';

const PaymentList = (props) => {
  const history = useHistory();
  const location = useLocation();
  const urlParams = useQuery();
  const dispatch = useDispatch();
  const classes = useStyles();
  const statusClasses = statusStyles();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const stateParams = location.state || {};
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...location.state,
    ...stateParams,
    ...urlParams,
  });

  // Check if we need to call payments or transaction
  const { orgType } = useContext(UserContext);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const core = useSelector((state) => state.config?.settings?.core || {});
  const hasPartnership = core?.partnership?.enabled || false;
  const isFFBEnabled = sharingType === 'FFB' && hasPartnership;
  const useTransactions = isFFBEnabled;

  const handleParams = (params) => {
    const data = { ...params };
    setParams(data);
    const _params = toQueryString(data);
    history.replace(buildUrl(`/payments${_params}`));
  };

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  const handleRowClick = (row) => {
    history.push(buildUrl(`/payments/${row?.id}`));
  };

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  const handleSearch = (name, value, res) => {
    let searchParams = {
      ...params,
      page: 1,
      ...(useTransactions
        ? {
            [name]: name === 'transaction_id' ? res?.transaction_id : value,
          }
        : { [name]: name === 'account_id' ? res?.account_id : value }),
    };

    Object.keys(searchParams).forEach(
      (key) => !searchParams[key] && searchParams[key] !== false && delete searchParams[key]
    );
    handleParams({ ...searchParams });
  };

  const {
    payments,
    transactions,
    organizations: { localeFormat },
  } = useSelector((state) => ({
    payments: state.payment,
    transactions: state.transaction,
    organizations: state.organizations,
  }));

  const data = useTransactions ? transactions.list : payments.payments;
  const pagination = useTransactions ? transactions.pagination : payments.pagination;
  const loading = useTransactions
    ? transactions.fetchingTransactions === FETCH_STATE.LOADING
    : payments.fetchingPayments;

  const fetchPayments = useCallback(
    (params = {}) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      if (useTransactions) {
        dispatch(
          TransactionsAction.fetchTransactions(params, { signal: abortController?.current?.signal })
        );
      } else {
        dispatch(
          PaymentActions.fetchPayments(params, { signal: abortController?.current?.signal })
        );
      }
    },
    [dispatch]
  );

  const fetchPaymentsDebounces = useCallback(
    debounce((params) => fetchPayments(params), 1000),
    [fetchPayments]
  );

  // Run once on mount, no debounce
  useEffect(() => {
    fetchPayments(params);
  }, []);

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchPaymentsDebounces(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchPaymentsDebounces, params]);

  const paymentColumns = [
    {
      name: i18n.t('payments.invoice_id'),
      selector: 'invoice.id',
      grow: 0,
    },
    {
      name: i18n.t('common.payment_id'),
      selector: 'id',
      grow: 0,
      sortable: true,
      minWidth: '140px',
    },
    {
      name: i18n.t('common.date'),
      selector: 'modified_time',
      sortable: true,
      format: (row) =>
        row?.modified_time ? moment(row?.modified_time).format(localeFormat) : '--',
      grow: 0,
    },
    {
      name:
        orgType === 'msOwnerOrg' ? i18n.t('notifications.partner') : i18n.t('payments.customer'),
      selector: 'account_id',
      format: (row) => row?.account_customer?.customer_name || '--',
      sortable: true,
      grow: 1,
    },
    {
      name: i18n.t('payments.method'),
      selector: 'source_type',
      format: (row) =>
        row?.source_type
          ? i18n.t(`payment_methods.${row?.source_type}`, {
              defaultValue: row?.source_type,
            })
          : '--',
      sortable: true,
      grow: 1,
    },
    {
      name: i18n.t('payments.paid'),
      selector: 'amount',
      format: (row) => `$${formatAmount(row.amount)}`,
      sortable: true,
      grow: 0,
    },
    {
      name: i18n.t('common.status'),
      selector: 'charge_status',
      format: (row) =>
        row?.status
          ? i18n.t(`payment_statuses.${row?.status}`, {
              defaultValue: row?.status,
            })
          : '--',
      sortable: true,
      grow: 0,
      minWidth: '170px',
    },
  ];

  const transactionColumns = [
    {
      name: i18n.t('transactions.transaction_id'),
      selector: 'id',
      grow: 0,
      sortable: true,
      minWidth: '160px',
    },
    {
      name: i18n.t('transactions.account_statement_id'),
      selector: 'account_statement.id',
      grow: 1,
      sortable: true,
      minWidth: '160px',
    },
    {
      name: i18n.t('common.date'),
      selector: 'modified_time',
      format: (row) =>
        row?.modified_time ? moment(row?.modified_time).format(localeFormat) : '--',
      grow: 1,
    },
    {
      name: i18n.t('payments.customer'),
      selector: 'account_customer.customer_name',
      grow: 1,
    },
    {
      name: i18n.t('payments.method'),
      selector: 'payment_method',
      format: (row) =>
        row?.source_type
          ? i18n.t(`payment_methods.${row?.source_type}`, {
              defaultValue: row?.source_type,
            })
          : '--',
      sortable: true,
      grow: 1,
    },
    {
      name: i18n.t('payments.paid'),
      selector: 'amount',
      format: (row) => `$${formatAmount(row.amount)}`,
      sortable: true,
      grow: 1,
    },
    {
      name: i18n.t('common.status'),
      selector: 'status',
      format: (row) => (
        <span className={statusClasses[row.status]}>
          {row?.status
            ? i18n.t(`payment_statuses.${row.status}`, { defaultValue: row.status })
            : '--'}
        </span>
      ),
      sortable: true,
      grow: 1,
      minWidth: '170px',
    },
  ];

  return (
    <Layout title={i18n.t('payments.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Title title={i18n.t('payments.title')} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            {useTransactions ? (
              <SearchField
                margin="none"
                label={`${i18n.t('transactions.search_account_statement')}`}
                field={{
                  onChange: handleSearch,
                  name: 'account_statement_id',
                  value: params.account_statement_id,
                }}
                type="number"
              />
            ) : (
              <SearchField
                margin="none"
                label={`${i18n.t('payments.search_invoice')}`}
                field={{ onChange: handleSearch, name: 'invoice_id', value: params.invoice_id }}
                type="number"
              />
            )}
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            {orgType !== 'msOwnerOrg' ? (
              <UsersSelect
                form={{
                  setFieldValue: handleSearch,
                }}
                type="users"
                margin="none"
                selector="account_id"
                field={{
                  name: 'account_id',
                  value: params.account_id || '',
                }}
                label={i18n.t('payments.customer')}
                firstLoad={params.account_id ? true : false}
              />
            ) : (
              <PartnersSelect
                form={{ setFieldValue: handleSearch }}
                label={i18n.t('notifications.search_by_partner')}
                field={{
                  name: 'organization_id',
                  value: params.organization_id,
                }}
                margin="none"
              />
            )}
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <PaymentMethodSelect
              form={{ setFieldValue: handleSearch }}
              field={{ name: 'source_type', value: params.source_type }}
              label={i18n.t('payments.method')}
              margin="none"
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <PaymentStatusSelect
              form={{ setFieldValue: handleSearch }}
              field={{ name: 'status', value: params.status }}
              label={i18n.t('common.status')}
              margin="none"
            />
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={data}
                columns={useTransactions ? transactionColumns : paymentColumns}
                loading={loading}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default PaymentList;
